import type { Dicom } from '@/models/dicom';
import { reconcilePinnedPanes } from './pinnedPanes';

/**
 * Pane navigation model (AP-804).
 *
 * Pane contents and the navigation cursor are independent. The cursor is an
 * index into the viewer's (filtered) DICOM list and only moves on a successful
 * normal navigation. Normal navigation refills the unpinned panes from the
 * cursor; pinned panes are never touched, and pinning/unpinning never changes
 * what any pane shows.
 */

/**
 * Visible pane slots that normal navigation may fill. Empty = fully pinned
 * grid, so `[0] ?? null` is the available pane (never a fake pane 0).
 */
export function listUnpinnedPanes(
  viewportCount: number,
  pinnedSlots: ReadonlySet<number>
): number[] {
  return Array.from({ length: viewportCount }, (_, i) => i).filter(
    (i) => !pinnedSlots.has(i)
  );
}

/** UUIDs of the DICOMs displayed in pinned panes. */
export function getPinnedUuids(
  paneDicoms: readonly (Dicom | null)[],
  pinnedSlots: ReadonlySet<number>
): Set<string> {
  const uuids = new Set<string>();
  pinnedSlots.forEach((slot) => {
    const dicom = paneDicoms[slot];
    if (dicom) uuids.add(dicom.uuid);
  });
  return uuids;
}

/** Indices into `dicoms` that normal navigation may place (not shown in a pinned pane). */
export function listNavigableIndices(
  dicoms: readonly Dicom[],
  pinnedUuids: ReadonlySet<string>
): number[] {
  const indices: number[] = [];
  dicoms.forEach((d, i) => {
    if (!pinnedUuids.has(d.uuid)) indices.push(i);
  });
  return indices;
}

/** Position of the cursor within `navigable`: the first entry at or after it. */
export function getCursorPosition(
  navigable: readonly number[],
  cursor: number
): number {
  const pos = navigable.findIndex((i) => i >= cursor);
  return pos === -1 ? 0 : pos;
}

/**
 * Refill every unpinned pane from the cursor. Pinned panes keep their DICOM.
 * On a partial last page the fill wraps to the start (end→start loop), but
 * only when there are more navigable DICOMs than panes — otherwise a single
 * page can't be filled without showing the same DICOM twice. Returns
 * `paneDicoms` itself when nothing changes, so a no-op fill doesn't re-render.
 */
export function fillUnpinnedPanes(
  paneDicoms: (Dicom | null)[],
  viewportCount: number,
  pinnedSlots: ReadonlySet<number>,
  dicoms: readonly Dicom[],
  cursor: number
): (Dicom | null)[] {
  const navigable = listNavigableIndices(
    dicoms,
    getPinnedUuids(paneDicoms, pinnedSlots)
  );
  const pageSize = Math.max(
    1,
    listUnpinnedPanes(viewportCount, pinnedSlots).length
  );
  const shouldWrap = navigable.length > pageSize;
  let pos = getCursorPosition(navigable, cursor);

  const filled = Array.from({ length: viewportCount }, (_, slot) => {
    if (pinnedSlots.has(slot)) return paneDicoms[slot] ?? null;
    const index = shouldWrap
      ? navigable[pos % navigable.length]
      : navigable[pos];
    pos += 1;
    return index === undefined ? null : dicoms[index];
  });
  const unchanged =
    filled.length === paneDicoms.length &&
    filled.every((d, i) => d === paneDicoms[i]);
  return unchanged ? paneDicoms : filled;
}

/**
 * Fit pins into a resized grid (#374): a pin keeps its pane where the new grid
 * still has one and reflows into the lowest free pane otherwise; pins the new
 * grid has no pane for are dropped. Because this model keeps pane content in
 * `paneDicoms` rather than in the pin itself, a reflowed pin has to carry its
 * DICOM to the new slot — so this returns both halves. Unpinned slots are left
 * as they are for `fillUnpinnedPanes` to refill. Returns the given references
 * when the pins already fit, so a no-op resize doesn't re-render.
 */
export function reconcilePinsForViewportCount(
  paneDicoms: (Dicom | null)[],
  pinnedSlots: ReadonlySet<number>,
  viewportCount: number
): { pinnedSlots: ReadonlySet<number>; paneDicoms: (Dicom | null)[] } {
  const before = new Map<number, Dicom>();
  pinnedSlots.forEach((slot) => {
    const dicom = paneDicoms[slot];
    if (dicom) before.set(slot, dicom);
  });

  const after = reconcilePinnedPanes(before, viewportCount);
  // Identity means every pin already fits on its own pane.
  if (after === before && before.size === pinnedSlots.size) {
    return { pinnedSlots, paneDicoms };
  }

  return {
    pinnedSlots: new Set(after.keys()),
    paneDicoms: Array.from(
      { length: viewportCount },
      (_, slot) => after.get(slot) ?? paneDicoms[slot] ?? null
    ),
  };
}

/**
 * Cursor after paging one page forward (+1) or back (-1), wrapping at the
 * ends. Paging strides from wherever the cursor is, so after a filmstrip click
 * it may sit off a page boundary.
 */
export function stepCursor(
  navigable: readonly number[],
  cursor: number,
  pageSize: number,
  direction: 1 | -1
): number {
  if (navigable.length === 0) return cursor;
  const pos = getCursorPosition(navigable, cursor);

  let nextPos: number;
  if (direction === 1) {
    nextPos = pos + pageSize;
    if (nextPos >= navigable.length) nextPos = 0;
  } else if (pos <= 0) {
    const pages = Math.ceil(navigable.length / pageSize);
    nextPos = Math.max(0, (pages - 1) * pageSize);
  } else {
    nextPos = Math.max(0, pos - pageSize);
  }
  return navigable[nextPos];
}
